export const translations = {
  en: {
    nav: { platform: "Platform", support: "Support", privacy: "Privacy", contact: "Contact", access: "Sign in" },
    footer: {
      tagline: "A platform to launch food apps per restaurant, connected to a central database and Stripe Connect payments.",
      colPlatform: "Platform",
      colResources: "Resources",
      how: "How it works",
      requestApp: "Request my app",
      support: "Support",
      contact: "Contact",
      privacy: "Privacy",
      rights: "Domifood Platform. All rights reserved.",
      madeWith: "Built with Supabase · Stripe Connect",
    },
    landing: {
      eyebrow: "For restaurants & businesses",
      h1: ["Your business,", "your own app."],
      heroDesc: "Do you have a food business and want your own ordering app? We build it for you — connected to payments and a central system, with your brand and your customers.",
      ctaPrimary: "I want my app",
      ctaSecondary: "Talk to sales",
      compareTitle: "Why your own app and not another platform?",
      compareSub: "Big delivery apps take a huge cut and keep your customers. With Domifood, the business is yours.",
      otherTitle: "Other apps",
      otherPoints: [
        "They charge you up to 30% per order",
        "The customers belong to the app, not to you",
        "Your brand stays hidden among hundreds",
        "You depend on their rules and fees",
      ],
      domiTitle: "With Domifood",
      domiPoints: [
        "Only 7% commission per order",
        "The customers are 100% yours",
        "Your own app with your brand on the App Store and Google Play",
        "You control your menu, prices and promotions",
      ],
      featuresTitle: "Everything your operation needs",
      features: [
        { title: "Your own app on the stores", desc: "Each business gets its own app published on the App Store and Google Play." },
        { title: "Only 7% commission", desc: "Every transaction computes and keeps a fair platform commission." },
        { title: "Secure payments", desc: "Charge with Stripe: net to your business, commission to the platform." },
        { title: "Your brand", desc: "Colors, logo, banner and menu customized for your business." },
        { title: "Your customers", desc: "The customer data and relationship stay with your business." },
        { title: "We help you launch", desc: "We set up and publish your app so you only worry about selling." },
      ],
      ctaTitle: "Ready for your own app?",
      ctaDesc: "Tell us about your business and we'll help you launch your app with only 7% commission.",
      ctaContact: "Request my app",
    },
    privacy: {
      eyebrow: "Legal",
      title: "Privacy Policy",
      intro: "Your privacy matters to us. This policy explains what data we collect, how we use it and what rights you have.",
      updated: "Last updated:",
      sections: [
        { title: "1. Information we collect", ps: [
          "We collect the information needed to operate the platform: account data (name, email), restaurant data (menus, categories, inventory) and order data (items, customer name and phone for delivery).",
          "Payments are processed securely through Stripe; we do not store full card data on our servers.",
        ] },
        { title: "2. How we use your data", ps: [
          "We use data to provide the service: showing menus, processing orders and payments, calculating the platform commission and offering support. We do not sell your personal data to third parties.",
        ] },
        { title: "3. Storage and security", ps: [
          "Data is hosted on Supabase (PostgreSQL) with per-restaurant isolation via row-level security (RLS). We apply reasonable technical and organizational measures to protect the information.",
        ] },
        { title: "4. Third parties", ps: [
          "We use trusted providers for specific functions: Stripe (payments), Supabase (database and image storage). Each processes data under its own policies.",
        ] },
        { title: "5. Limitation of liability", ps: [
          "Domifood is solely a software provider: we offer the application and technology infrastructure each restaurant uses to display its menu and manage orders and payments. We are not responsible for the food or any aspect related to it.",
          "In particular, and without limitation, Domifood is not responsible for:",
        ], list: [
          "The preparation, quality, hygiene, ingredients, freshness or condition of the food.",
          "Any case of food poisoning, allergic reaction, illness or health damage arising from consuming the food.",
          "The delivery service: times, conditions, losses, or the person/company that delivers.",
          "How the food arrives to the customer (temperature, packaging, presentation or any alteration during transport).",
        ], after: "All responsibility over the food, its preparation, handling, delivery and final condition rests exclusively with each restaurant. Domifood only provides the restaurant's app service and does not intervene in the culinary or logistics operation." },
        { title: "6. Your rights", ps: [
          "You can request access, correction or deletion of your personal data by writing to us through the Contact page. We will respond within a reasonable time under applicable law.",
        ] },
        { title: "7. Changes to this policy", ps: [
          "We may update this policy occasionally. We will publish the current version on this page with its update date.",
        ] },
      ],
    },
    support: {
      eyebrow: "Help",
      title: "Support Center",
      intro: "Find quick answers or write to us. We're here to help you sell more and operate without friction.",
      emailCard: { title: "Email support", desc: "We reply within 24 business hours.", value: "support@domifood.app" },
      contactCard: { title: "Prefer to write to us?", desc: "Tell us your case and we'll get in touch.", link: "Go to Contact →" },
      faqTitle: "Frequently asked questions",
      faqs: [
        { q: "How do I add a restaurant?", a: "From the Admin Panel you can create restaurants, assign owners and configure their menu, categories and ingredients." },
        { q: "How do payments and commission work?", a: "Payments are processed with Stripe Connect. The net goes to the restaurant and the platform automatically keeps its 7% commission." },
        { q: "Can I use the panel on a tablet?", a: "Yes. The Restaurant Panel can be installed as an app on tablets using the install button on the sign-in screen." },
        { q: "How do I edit the menu and ingredients?", a: "In the Restaurant Panel, Menu tab: create categories with a photo, add dishes with price, photo and ingredients the customer can remove." },
      ],
    },
    contact: {
      eyebrow: "Contact",
      title: "Let's talk",
      intro: "Have questions or want to add your restaurant? Write to us and we'll reply soon.",
      email: "Email",
      phone: "Phone",
      office: "Office",
      name: "Name",
      subject: "Subject",
      subjectPh: "e.g. I want to add my restaurant",
      message: "Message",
      send: "Send message",
      successTitle: "Message received!",
      successDesc: "Thanks for reaching out. We'll reply very soon.",
      again: "Send another message",
    },
  },
  es: {
    nav: { platform: "Plataforma", support: "Soporte", privacy: "Privacidad", contact: "Contacto", access: "Acceso" },
    footer: {
      tagline: "Una plataforma para lanzar apps de comida por restaurante, conectadas a una base de datos central y a pagos con Stripe Connect.",
      colPlatform: "Plataforma",
      colResources: "Recursos",
      how: "Cómo funciona",
      requestApp: "Solicitar mi app",
      support: "Soporte",
      contact: "Contacto",
      privacy: "Privacidad",
      rights: "Domifood Platform. Todos los derechos reservados.",
      madeWith: "Hecho con Supabase · Stripe Connect",
    },
    landing: {
      eyebrow: "Para restaurantes y negocios",
      h1: ["Tu negocio,", "tu propia app."],
      heroDesc: "¿Tienes un negocio de comida y quieres tu propia app de pedidos? Nosotros la creamos por ti — conectada a pagos y a un sistema central, con tu marca y tus clientes.",
      ctaPrimary: "Quiero mi app",
      ctaSecondary: "Hablar con ventas",
      compareTitle: "¿Por qué tu propia app y no otra plataforma?",
      compareSub: "Las grandes apps de delivery se llevan una comisión enorme y se quedan con tus clientes. Con Domifood, el negocio es tuyo.",
      otherTitle: "Otras apps",
      otherPoints: [
        "Te cobran hasta un 30% por pedido",
        "Los clientes son de la app, no tuyos",
        "Tu marca queda escondida entre cientos",
        "Dependes de sus reglas y comisiones",
      ],
      domiTitle: "Con Domifood",
      domiPoints: [
        "Solo 7% de comisión por pedido",
        "Los clientes son 100% tuyos",
        "Tu propia app con tu marca en App Store y Google Play",
        "Tú controlas tu menú, precios y promociones",
      ],
      featuresTitle: "Todo lo que necesita tu operación",
      features: [
        { title: "Tu propia app en las tiendas", desc: "Cada negocio tiene su propia app publicada en App Store y Google Play." },
        { title: "Solo 7% de comisión", desc: "Cada transacción calcula y retiene una comisión justa de plataforma." },
        { title: "Pagos seguros", desc: "Cobra con Stripe: neto a tu negocio, comisión a la plataforma." },
        { title: "Tu marca", desc: "Colores, logo, banner y menú personalizados para tu negocio." },
        { title: "Tus clientes", desc: "Los datos y la relación con el cliente quedan en tu negocio." },
        { title: "Te ayudamos a lanzar", desc: "Configuramos y publicamos tu app para que solo te preocupes por vender." },
      ],
      ctaTitle: "¿Listo para tu propia app?",
      ctaDesc: "Cuéntanos sobre tu negocio y te ayudamos a lanzar tu app con solo 7% de comisión.",
      ctaContact: "Solicitar mi app",
    },
    privacy: {
      eyebrow: "Legal",
      title: "Política de Privacidad",
      intro: "Tu privacidad es importante para nosotros. Esta política explica qué datos recopilamos, cómo los usamos y qué derechos tienes.",
      updated: "Última actualización:",
      sections: [
        { title: "1. Información que recopilamos", ps: [
          "Recopilamos la información necesaria para operar la plataforma: datos de cuenta (nombre, correo electrónico), datos de los restaurantes (menús, categorías, inventario), y datos de pedidos (artículos, nombre y teléfono del cliente para la entrega).",
          "Los pagos se procesan de forma segura a través de Stripe; no almacenamos los datos completos de las tarjetas en nuestros servidores.",
        ] },
        { title: "2. Cómo usamos tus datos", ps: [
          "Usamos los datos para prestar el servicio: mostrar menús, procesar pedidos y pagos, calcular la comisión de la plataforma y ofrecer soporte. No vendemos tus datos personales a terceros.",
        ] },
        { title: "3. Almacenamiento y seguridad", ps: [
          "Los datos se alojan en Supabase (PostgreSQL) con aislamiento por restaurante mediante políticas de seguridad a nivel de fila (RLS). Aplicamos medidas técnicas y organizativas razonables para proteger la información.",
        ] },
        { title: "4. Terceros", ps: [
          "Utilizamos proveedores de confianza para funciones específicas: Stripe (pagos), Supabase (base de datos y almacenamiento de imágenes). Cada uno trata los datos conforme a sus propias políticas.",
        ] },
        { title: "5. Limitación de responsabilidad", ps: [
          "Domifood es únicamente un proveedor de software: ofrecemos la aplicación y la infraestructura tecnológica que cada restaurante utiliza para mostrar su menú y gestionar pedidos y pagos. No somos responsables de la comida ni de ningún aspecto relacionado con ella.",
          "En particular, y sin limitarse a ello, Domifood no se hace responsable de:",
        ], list: [
          "La preparación, calidad, higiene, ingredientes, frescura o estado de la comida.",
          "Cualquier caso de intoxicación, reacción alérgica, enfermedad o daño a la salud derivado del consumo de los alimentos.",
          "El servicio de entrega (delivery): tiempos, condiciones, extravíos o la persona/empresa que realiza la entrega.",
          "Cómo llega la comida al cliente (temperatura, empaque, presentación o cualquier alteración durante el transporte).",
        ], after: "Toda la responsabilidad sobre los alimentos, su elaboración, manipulación, entrega y estado final recae exclusivamente en cada restaurante. Domifood solo brinda el servicio de la aplicación del restaurante y no interviene en la operación gastronómica ni logística." },
        { title: "6. Tus derechos", ps: [
          "Puedes solicitar acceso, corrección o eliminación de tus datos personales escribiéndonos a través de la página de Contacto. Responderemos en un plazo razonable conforme a la ley aplicable.",
        ] },
        { title: "7. Cambios en esta política", ps: [
          "Podemos actualizar esta política ocasionalmente. Publicaremos la versión vigente en esta página con su fecha de actualización.",
        ] },
      ],
    },
    support: {
      eyebrow: "Ayuda",
      title: "Centro de Soporte",
      intro: "Encuentra respuestas rápidas o escríbenos. Estamos para ayudarte a vender más y operar sin fricción.",
      emailCard: { title: "Soporte por correo", desc: "Respondemos en un plazo de 24 horas hábiles.", value: "soporte@domifood.app" },
      contactCard: { title: "¿Prefieres escribirnos?", desc: "Cuéntanos tu caso y te contactamos.", link: "Ir a Contacto →" },
      faqTitle: "Preguntas frecuentes",
      faqs: [
        { q: "¿Cómo agrego un restaurante?", a: "Desde el Panel Admin puedes crear restaurantes, asignar propietarios y configurar su menú, categorías e ingredientes." },
        { q: "¿Cómo funcionan los pagos y la comisión?", a: "Los pagos se procesan con Stripe Connect. El neto llega al restaurante y la plataforma retiene automáticamente su comisión del 7%." },
        { q: "¿Puedo usar el panel en una tablet?", a: "Sí. El Panel Restaurante se puede instalar como app (PWA) en tablets con el botón “Instalar app para tablet” en la pantalla de acceso." },
        { q: "¿Cómo edito el menú y los ingredientes?", a: "En el Panel Restaurante, pestaña Menú: crea categorías con foto, agrega platos con precio, foto e ingredientes que el cliente puede quitar." },
      ],
    },
    contact: {
      eyebrow: "Contacto",
      title: "Hablemos",
      intro: "¿Tienes preguntas o quieres sumar tu restaurante? Escríbenos y te respondemos pronto.",
      email: "Correo",
      phone: "Teléfono",
      office: "Oficina",
      name: "Nombre",
      subject: "Asunto",
      subjectPh: "Ej. Quiero sumar mi restaurante",
      message: "Mensaje",
      send: "Enviar mensaje",
      successTitle: "¡Mensaje recibido!",
      successDesc: "Gracias por contactarnos. Te responderemos muy pronto.",
      again: "Enviar otro mensaje",
    },
  },
};
